import React, { useEffect, useState, useRef } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { useGlobalContext } from "../Config";
import { InputText } from 'primereact/inputtext';
import FormEditarPlantilla from "./FormEditarPlantilla";
import FormSubirPlantilla from "../components/FormSubirPlantilla";

function TablePlantillas({
    isFormVisibleEditPlantilla, 
    setIsFormVisibleEditPlantilla
}) {
    const { urlContratos } = useGlobalContext();
    const toast = useRef(null);
    const [plantillas, setPlantillas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showEditDialogPlantilla, setShowEditDialogPlantilla] = useState(false);
    const [selectedPlantilla, setSelectedPlantilla] = useState(null);
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [showOverwriteDialog, setShowOverwriteDialog] = useState(false);
    const [plantillaToOverwrite, setPlantillaToOverwrite] = useState(null);
    const [overwriteFile, setOverwriteFile] = useState(null);
    const [loadingOverwrite, setLoadingOverwrite] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const onCellEditComplete = async (e) => {
        const { rowData, field } = e;
        const newValue = e.newValue !== undefined ? e.newValue : e.value;
        if (!rowData?.id) return;
        if (field !== 'nombre' && field !== 'tipo_mime') return;

        const updatedNombre = field === 'nombre' ? newValue : rowData.nombre;
        const updatedTipoMime = field === 'tipo_mime' ? newValue : rowData.tipo_mime;
        const idUsuario = sessionStorage.getItem('usuario_id') || '';

        try {
            const url = `${urlContratos}editarPlantillaDocumento` +
                `?id=${encodeURIComponent(rowData.id)}` +
                `&nombre=${encodeURIComponent(updatedNombre)}` +
                `&tipoMime=${encodeURIComponent(updatedTipoMime)}` +
                `&idAdjunto=${encodeURIComponent(rowData.idAdjunto || '')}` +
                `&idTipoDocumento=${encodeURIComponent(rowData.idTipoDocumento || '')}` +
                `&idUsuario=${encodeURIComponent(idUsuario)}`;

            const resp = await fetch(url, {
                method: 'PUT',
                credentials: 'include',
            });
            if (!resp.ok) throw new Error(`Error al editar plantilla (${resp.status})`);

            setPlantillas(prev =>
                prev.map(p => (p.id === rowData.id ? { ...p, nombre: updatedNombre, tipo_mime: updatedTipoMime } : p))
            );
            toast.current?.show({ severity: 'success', summary: 'Éxito', detail: 'Plantilla actualizada correctamente', life: 3000 });
        } catch (err) {
            console.error('No se pudo editar la plantilla:', err);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo actualizar la plantilla', life: 3000 });
        }
    };

    const fetchPlantillas = async () => {
        try {
            setLoading(true);
            const resp = await fetch(`${urlContratos}getPlantillasDocumentoByEmpresa`, {
                method: 'GET',
                credentials: 'include',
            });
            const data = await resp.json();
            const lista = Array.isArray(data.plantillasDocumentos)
                ? data.plantillasDocumentos
                : Object.values(data.plantillasDocumentos || {});
            setPlantillas(lista);
        } catch (err) {
            console.error("Error al obtener plantillas de documento:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isFormVisibleEditPlantilla) return;
        fetchPlantillas();
    }, [isFormVisibleEditPlantilla, urlContratos]);

    const deletePlantilla = async (rowData) => {
        try {
            if (!rowData?.id) return;
            const idUsuario = sessionStorage.getItem('usuario_id') || '';
            const resp = await fetch(`${urlContratos}cambiarEstadoPlantillaDocumento?idPlantilla=${rowData.id}&activo=0&idUsuario=${idUsuario}`, {
                method: 'PUT',
                credentials: 'include',
            });
            if (!resp.ok) {
                throw new Error(`Error al eliminar plantilla (${resp.status})`);
            }
            setPlantillas(prev =>
                prev.map(p => (p.id === rowData.id ? { ...p, activo: false } : p))
            );
            toast.current?.show({ severity: 'success', summary: 'Éxito', detail: 'Plantilla desactivada correctamente', life: 3000 });
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'No se pudo desactivar la plantilla ya que debe haber una por tipo de documento', life: 3000 });
        }
    };

    const downloadPlantilla = async (rowData) => {
        try {
            if (!rowData?.id) {
                throw new Error('Plantilla inválida para descargar');
            }

            const resp = await fetch(`${urlContratos}descargarPlantilla?id=${rowData.id}`, {
                method: 'GET',
                credentials: 'include',
            });

            if (!resp.ok) {
                throw new Error(`Error al descargar plantilla (${resp.status})`);
            }

            const blob = await resp.blob();
            const contentDisposition = resp.headers.get('content-disposition') || '';
            const match = contentDisposition.match(/filename\*?=(?:UTF-8''|\")?([^\";]+)/i);
            const fileName = match
                ? decodeURIComponent(match[1].replace(/\"/g, ''))
                : `plantilla-${rowData.id}`;

            const fileUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = fileUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(fileUrl);
        } catch (err) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: err.message || 'No se pudo descargar la plantilla',
                life: 3000,
            });
        }
    };

    const toggleFormVisibility = () => {
        if (isFormVisible) {
            setIsFormVisible(false);
        } else {
            setIsFormVisible(true);
        }
    };

    const setSelectedOverwriteFile = (file) => {
        if (!file) {
            setOverwriteFile(null);
            return;
        }

        const isDocx =
            file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
            /\.docx$/i.test(file.name);

        if (!isDocx) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Archivo no permitido',
                detail: 'Solo se aceptan documentos .docx',
                life: 3000,
            });
            setOverwriteFile(null);
            return;
        }

        setOverwriteFile(file);
    };

    const openOverwriteDialog = (rowData) => {
        setPlantillaToOverwrite(rowData);
        setOverwriteFile(null);
        setIsDragOver(false);
        setShowOverwriteDialog(true);
    };

    const closeOverwriteDialog = () => {
        setShowOverwriteDialog(false);
        setPlantillaToOverwrite(null);
        setOverwriteFile(null);
        setIsDragOver(false);
    };

    const handleOverwritePlantilla = async () => {
        try {
            if (!plantillaToOverwrite?.id) {
                throw new Error('Id Plantilla inválida para sobreescribir');
            }
            if (!overwriteFile) {
                throw new Error('Debe seleccionar un archivo .docx');
            }

            setLoadingOverwrite(true);

            const formData = new FormData();
            formData.append('idPlantillaAnterior', plantillaToOverwrite.id);
            formData.append('empresa', plantillaToOverwrite.nombre_empresa);
            formData.append('nombre', overwriteFile.name);
            formData.append('idTipoDocumento', plantillaToOverwrite.id_tipo_documento);
            formData.append('idUsuario', sessionStorage.getItem('usuario_id') || '');
            formData.append('file', overwriteFile);

            const response = await fetch(`${urlContratos}sobreescribirPlantilla`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error(`Error al sobreescribir plantilla (${response.status})`);
            }

            toast.current?.show({ severity: 'success', summary: 'Éxito', detail: 'Plantilla sobreescrita correctamente', life: 3000 });
            closeOverwriteDialog();
            await fetchPlantillas();
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: err.message || 'No se pudo sobreescribir la plantilla', life: 3500 });
        } finally {
            setLoadingOverwrite(false);
        }
    };

    const onGlobalFilterChange = (e) => {
        setGlobalFilterValue(e.target.value || '');
    };

    const renderHeader = () => {
        return (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-2">
            <span className="p-input-icon-left" style={{display: "flex", alignItems: "center", justifyContent: "center"}}>
            <i className="pi pi-search" />
                <InputText
                    value={globalFilterValue}
                    onChange={onGlobalFilterChange}
                    placeholder="Buscar por nombre"
                />
            </span>
            <span className="p-input-icon-left ml-2" style={{display: "flex", alignItems: "center", justifyContent: "flex-end"}}>
                <Button 
                    label="Crear Plantilla"
                    icon="pi pi-plus"
                    onClick={toggleFormVisibility}
                    className="p-button-sm p-component mr-2 p-button-rounded p-button-primary "
                    disabled={loading}
                    raised
                />
            </span>
        </div>
        );
    };

    const header = renderHeader();



    return (
        <>
            <Toast ref={toast} />
            <Dialog
                header="Mantenedor de Plantillas"
                visible={isFormVisibleEditPlantilla}
                onHide={() => setIsFormVisibleEditPlantilla(false)}
                className="p-fluid product-dialog"
            >
                
                <DataTable 
                    className="p-datatable-gridlines"
                    showGridlines
                    paginator
                    rows={10}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                    paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
                    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} plantillas"
                    dataKey="id"
                    emptyMessage="No se encontraron plantillas."
                    stripedRows="true"
                    globalFilter={globalFilterValue}
                    globalFilterFields={["nombre"]}
                    scrollHeight="57vh"
                    scrollable
                    value={plantillas}
                    loading={loading}
                    editMode="cell"
                    onCellEditComplete={onCellEditComplete}
                    header={header}
                    
                >
                    <Column
                        field="nombre"
                        header="Nombre"
                        sortable
                    />

                    <Column
                        field="nombre_empresa"
                        header="Empresa"
                        sortable
                    />

                    <Column
                        field="nombre_tipo"
                        header="Tipo"
                        sortable
                    />

                    <Column
                        field="activo"
                        header="Estados"
                        body={(rowData) => (
                        <>
                            <div>
                                {rowData.activo ? (
                                <Button
                                    icon="pi pi-check"
                                    className="p-button-rounded p-button-success p-button mr-2 mb-2 mt-2"
                                    outlined
                                    tooltip="plantilla activa"
                                    tooltipOptions={{ position: 'top' }}
                                />
                                ) : (
                                <Button
                                    icon="pi pi-times"
                                    className="p-button-rounded p-button-danger p-button mr-2 mb-2 mt-2"
                                    onClick={() => {}}
                                    outlined
                                    tooltip="plantilla inactiva"
                                    tooltipOptions={{ position: 'top' }}
                                />
                                )}
                            </div>
                        </>
                        )}
                        sortable
                    />
                    <Column
                        header="Acciones"
                        body={(rowData) => (
                        <>
                            <Button
                                icon="pi pi-pencil"
                                className="p-button-rounded p-button-success p-button mr-2 mb-2 mt-2"
                                onClick={() => {
                                    setSelectedPlantilla(rowData);
                                    setShowEditDialogPlantilla(true);
                                }}
                                outlined
                                disabled={!rowData.activo}
                            />
                            {/* <Button
                                icon="pi pi-times"
                                className="p-button-rounded p-button-danger p-button-outlined p-button mr-2 mb-2 mt-2"
                                tooltip="Eliminar Plantilla"
                                tooltipOptions={{ position: 'top' }}
                                onClick={() => deletePlantilla(rowData)}
                                disabled={!rowData.activo}
                            /> */}
                            <Button
                                icon="pi pi-download"
                                className="p-button-rounded p-button-info p-button-outlined p-button mr-2 mb-2 mt-2"
                                tooltip="Descargar Plantilla"
                                tooltipOptions={{ position: 'top' }}
                                onClick={() => downloadPlantilla(rowData)}
                                disabled={!rowData.activo}
                            />
                            <Button
                                icon="pi pi-upload"
                                className="p-button-rounded p-button-info p-button-outlined p-button mr-2 mb-2 mt-2"
                                tooltip="Sobre escribir Plantilla"
                                tooltipOptions={{ position: 'top' }}
                                onClick={() => openOverwriteDialog(rowData)}
                                disabled={!rowData.activo}
                            />

                        </>
                        )}
                    />
                </DataTable>

                <FormEditarPlantilla
                    visible={showEditDialogPlantilla}
                    onHide={() => setShowEditDialogPlantilla(false)}
                    plantilla={selectedPlantilla}
                    onSaved={(updated) => {
                        setPlantillas(prev =>
                            prev.map(p => (p.id === updated.id ? { ...p, ...updated } : p))
                        );
                        setShowEditDialogPlantilla(false);
                    }}
                    onReload={fetchPlantillas}
                />

                <FormSubirPlantilla
                    isFormVisible={isFormVisible}
                    setIsFormVisible={setIsFormVisible}
                    fetchDocumentos={fetchPlantillas}
                    toast={toast}
                />
            </Dialog>

            <Dialog
                header="Sobreescribir Plantilla"
                visible={showOverwriteDialog}
                onHide={closeOverwriteDialog}
                style={{ width: '32rem' }}
                modal
            >
                <div className="flex flex-column gap-3">
                    <span>
                        Selecciona el nuevo archivo para sobreescribir la plantilla <b>{plantillaToOverwrite?.nombre || ''}</b>
                    </span>
                    <div
                        onDragOver={(e) => {
                            e.preventDefault();
                            setIsDragOver(true);
                        }}
                        onDragLeave={() => setIsDragOver(false)}
                        onDrop={(e) => {
                            e.preventDefault();
                            setIsDragOver(false);
                            setSelectedOverwriteFile(e.dataTransfer.files?.[0] || null);
                        }}
                        style={{
                            border: `2px dashed ${isDragOver ? '#3b82f6' : '#cbd5e1'}`,
                            borderRadius: '8px',
                            padding: '1rem',
                            textAlign: 'center',
                            background: isDragOver ? '#eff6ff' : 'transparent',
                        }}
                    >
                        <p className="m-0 mb-2">Drag & drop .docx here</p>
                        <input
                            type="file"
                            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                            onChange={(e) => setSelectedOverwriteFile(e.target.files?.[0] || null)}
                        />
                        {overwriteFile && (
                            <small className="block mt-2">Selected: {overwriteFile.name}</small>
                        )}
                    </div>
                    <div className="flex justify-content-end gap-2 mt-2">
                        <Button
                            label="Cancelar"
                            className="p-button-text"
                            onClick={closeOverwriteDialog}
                            disabled={loadingOverwrite}
                        />
                        <Button
                            label="Sobreescribir"
                            icon={loadingOverwrite ? 'pi pi-spin pi-spinner' : 'pi pi-upload'}
                            className="p-button-warning"
                            onClick={handleOverwritePlantilla}
                            disabled={!overwriteFile || loadingOverwrite}
                        />
                    </div>
                </div>
            </Dialog>
        </>
    );
}

export default TablePlantillas;
